import React, { useEffect, useState } from 'react';
import { X, Loader2, Plus, Save, Globe, MapPin, Users2, Clock, Sparkles, Rocket, Pause, Play, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { fetchWithAuth } from './IntelligenceLayout';
import { StageBadge } from './CRMCompanies';
import CRMOutreachModal from './CRMOutreachModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CRMCompanyDetail = ({ companyId, config, onClose, onUpdated }) => {
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [activityText, setActivityText] = useState('');
  const [showAddContact, setShowAddContact] = useState(false);
  const [outreachContact, setOutreachContact] = useState(null);
  const [sequence, setSequence] = useState(null);
  const [seqLoading, setSeqLoading] = useState(false);

  const loadSequence = () => {
    fetchWithAuth(`${API_URL}/api/crm/sequences?company_id=${companyId}`)
      .then(d => setSequence((d.items || []).find(s => ['active', 'paused'].includes(s.status)) || (d.items || [])[0] || null))
      .catch(() => {});
  };

  const load = () => {
    setLoading(true);
    fetchWithAuth(`${API_URL}/api/crm/companies/${companyId}`)
      .then(c => { setCompany(c); setForm({ pipeline_stage: c.pipeline_stage, account_status: c.account_status, account_owner: c.account_owner || '', location: c.location || '', next_action: c.next_action || '', next_action_due_date: c.next_action_due_date || '', notes: c.notes || '', do_not_contact: !!c.do_not_contact, do_not_contact_reason: c.do_not_contact_reason || '' }); })
      .catch(() => toast.error('Could not load company')).finally(() => setLoading(false));
    loadSequence();
  };

  useEffect(() => { load(); }, [companyId]);

  const save = async () => {
    setSaving(true);
    try {
      await fetchWithAuth(`${API_URL}/api/crm/companies/${companyId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      toast.success('Saved');
      load();
      onUpdated && onUpdated();
    } catch (e) {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const logActivity = async () => {
    if (!activityText.trim()) return;
    try {
      await fetchWithAuth(`${API_URL}/api/crm/companies/${companyId}/activities`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: activityText, activity_type: 'note' }) });
      setActivityText('');
      load();
    } catch (e) {
      toast.error('Could not log activity');
    }
  };

  const startSequence = async () => {
    setSeqLoading(true);
    try {
      await fetchWithAuth(`${API_URL}/api/crm/sequences`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ company_id: companyId }) });
      toast.success('Sequence started — touch 1 of 7 will be drafted for your approval shortly');
      loadSequence();
    } catch (e) {
      toast.error(e?.message || 'Could not start sequence');
    } finally {
      setSeqLoading(false);
    }
  };

  const sequenceAction = async (action) => {
    setSeqLoading(true);
    try {
      await fetchWithAuth(`${API_URL}/api/crm/sequences/${sequence.id}/${action}`, { method: 'POST' });
      toast.success(`Sequence ${action}d`);
      loadSequence();
    } catch (e) {
      toast.error(e?.message || `Could not ${action} sequence`);
    } finally {
      setSeqLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="company-detail-drawer">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white w-full max-w-lg h-full shadow-2xl overflow-y-auto">
        {loading || !company ? (
          <div className="flex items-center justify-center h-full"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
        ) : (
          <div className="p-6 space-y-5">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-xl font-bold text-[#0A1F44]" data-testid="company-detail-name">{company.name}</h2>
                <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                  {company.website && <span className="flex items-center gap-1"><Globe className="h-3 w-3" /> {company.website}</span>}
                  {company.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {company.location}</span>}
                </div>
                <p className="text-xs text-gray-400 mt-1">Source: {company.source} {company.industry ? `· ${company.industry}` : ''} {company.company_size ? `· ${company.company_size} employees` : ''}</p>
              </div>
              <button onClick={onClose} data-testid="close-company-detail"><X className="h-5 w-5 text-gray-400" /></button>
            </div>

            {company.signal_summary && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800" data-testid="company-signal-summary">
                <b>Signal Feed context:</b> {company.signal_summary}
              </div>
            )}

            {company.potential_duplicate_domain && (
              company.duplicate_review_status === 'confirmed_not_duplicate' ? (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-gray-500" data-testid="company-duplicate-reviewed">
                  <b>Reviewed — kept separate.</b> Shares domain "{company.potential_duplicate_domain}" with: {(company.potential_duplicate_matches || []).map(m => m.name).join(', ')}. {company.duplicate_review_note}
                </div>
              ) : (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700" data-testid="company-duplicate-warning">
                  <b>Possible duplicate</b> — shares domain "{company.potential_duplicate_domain}" with: {(company.potential_duplicate_matches || []).map(m => m.name).join(', ')}. Not auto-merged; review manually.
                </div>
              )
            )}

            <div className="grid grid-cols-2 gap-3">
              <Field label="Pipeline Stage">
                <select value={form.pipeline_stage || ''} onChange={e => setForm({ ...form, pipeline_stage: e.target.value })} data-testid="edit-pipeline-stage"
                  className="w-full text-sm px-2 py-1.5 border border-gray-200 rounded-lg">
                  {config.pipeline_stages.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Account Status">
                <select value={form.account_status || ''} onChange={e => setForm({ ...form, account_status: e.target.value })} data-testid="edit-account-status"
                  className="w-full text-sm px-2 py-1.5 border border-gray-200 rounded-lg">
                  {config.account_statuses.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Account Owner">
                <input value={form.account_owner} onChange={e => setForm({ ...form, account_owner: e.target.value })} data-testid="edit-account-owner"
                  className="w-full text-sm px-2 py-1.5 border border-gray-200 rounded-lg" placeholder="Unassigned" />
              </Field>
              <div className="col-span-2">
                <Field label="Address (used on BoldSign agreements)">
                  <input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} data-testid="edit-location"
                    className="w-full text-sm px-2 py-1.5 border border-gray-200 rounded-lg" placeholder="e.g. 123 Main St, Chicago, IL" />
                </Field>
              </div>
              <Field label="Next Action Due">
                <input type="date" value={form.next_action_due_date} onChange={e => setForm({ ...form, next_action_due_date: e.target.value })} data-testid="edit-next-action-date"
                  className="w-full text-sm px-2 py-1.5 border border-gray-200 rounded-lg" />
              </Field>
              <div className="col-span-2">
                <Field label="Next Action">
                  <input value={form.next_action} onChange={e => setForm({ ...form, next_action: e.target.value })} data-testid="edit-next-action"
                    className="w-full text-sm px-2 py-1.5 border border-gray-200 rounded-lg" placeholder="e.g. Call decision-maker" />
                </Field>
              </div>
              <div className="col-span-2">
                <Field label="Notes">
                  <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} data-testid="edit-notes" rows={2}
                    className="w-full text-sm px-2 py-1.5 border border-gray-200 rounded-lg" />
                </Field>
              </div>
              <div className="col-span-2 bg-red-50 border border-red-100 rounded-lg p-3">
                <label className="flex items-center gap-2 text-sm font-medium text-red-700">
                  <input type="checkbox" checked={!!form.do_not_contact} onChange={e => setForm({ ...form, do_not_contact: e.target.checked })} data-testid="edit-do-not-contact" />
                  Do Not Contact (company-wide)
                </label>
                <p className="text-[11px] text-red-500 mt-0.5">Blocks all automated and scheduled outreach to every contact at this company.</p>
                {form.do_not_contact && (
                  <input value={form.do_not_contact_reason} onChange={e => setForm({ ...form, do_not_contact_reason: e.target.value })} placeholder="Reason (optional)"
                    data-testid="edit-do-not-contact-reason" className="w-full mt-2 text-sm px-2 py-1.5 border border-red-200 rounded-lg" />
                )}
              </div>
            </div>
            <button onClick={save} disabled={saving} data-testid="save-company-btn"
              className="w-full flex items-center justify-center gap-2 py-2 bg-[#0A1F44] text-white rounded-lg text-sm font-medium hover:bg-[#0A1F44]/90 disabled:opacity-50">
              <Save className="h-3.5 w-3.5" /> {saving ? 'Saving...' : 'Save Changes'}
            </button>

            <div className="border border-gray-100 rounded-lg p-3" data-testid="sequence-panel">
              <h3 className="text-sm font-semibold text-[#0A1F44] flex items-center gap-1.5 mb-2"><Rocket className="h-4 w-4" /> 7-Touch Sequence</h3>
              {sequence && ['active', 'paused'].includes(sequence.status) ? (
                <div>
                  <p className="text-xs text-gray-600" data-testid="sequence-status">
                    <span className={`font-medium ${sequence.status === 'active' ? 'text-emerald-600' : 'text-amber-600'}`}>{sequence.status === 'active' ? 'Active' : 'Paused'}</span>
                    {' '}— touch {sequence.current_touch} of 7 · targeting {sequence.primary_contact_name || 'primary decision-maker'}
                  </p>
                  <div className="flex gap-2 mt-2">
                    {sequence.status === 'active' ? (
                      <button onClick={() => sequenceAction('pause')} disabled={seqLoading} data-testid="pause-sequence-btn"
                        className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg border border-amber-200 text-amber-700 hover:bg-amber-50"><Pause className="h-3 w-3" /> Pause</button>
                    ) : (
                      <button onClick={() => sequenceAction('resume')} disabled={seqLoading} data-testid="resume-sequence-btn"
                        className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-50"><Play className="h-3 w-3" /> Resume</button>
                    )}
                    <button onClick={() => sequenceAction('cancel')} disabled={seqLoading} data-testid="cancel-sequence-btn"
                      className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg border border-red-200 text-red-600 hover:bg-red-50"><XCircle className="h-3 w-3" /> Cancel</button>
                  </div>
                </div>
              ) : (
                <div>
                  {sequence && (
                    <p className="text-[11px] text-gray-400 mb-1.5" data-testid="sequence-prior-status">Previous sequence: {sequence.status}{sequence.suspend_reason ? ` — ${sequence.suspend_reason}` : ''}</p>
                  )}
                  <button onClick={startSequence} disabled={seqLoading} data-testid="start-sequence-btn"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-[#0A1F44] text-white hover:bg-[#0A1F44]/90 disabled:opacity-50">
                    {seqLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Rocket className="h-3.5 w-3.5" />} Start 7-Touch Sequence
                  </button>
                  <p className="text-[11px] text-gray-400 mt-1">Requires an enriched decision-maker email on file. Every touch still needs your approval.</p>
                </div>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-[#0A1F44] flex items-center gap-1.5"><Users2 className="h-4 w-4" /> Contacts ({company.contacts.length})</h3>
                <button onClick={() => setShowAddContact(true)} data-testid="add-contact-btn" className="text-xs font-medium text-[#0A1F44] flex items-center gap-1 hover:underline">
                  <Plus className="h-3 w-3" /> Add
                </button>
              </div>
              {company.contacts.length === 0 ? (
                <p className="text-xs text-gray-400">No contacts yet.</p>
              ) : company.contacts.map(ct => {
                const readyToContact = ct.first_name !== 'Unknown' && (ct.email || ct.phone || ct.linkedin_url);
                return (
                <div key={ct.id} className="border border-gray-100 rounded-lg p-2.5 mb-1.5 text-sm" data-testid={`contact-item-${ct.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-[#0A1F44]">{ct.first_name} {ct.last_name || ''}</span>
                    {ct.is_decision_maker && <span className="text-[10px] px-1.5 py-0.5 bg-[#D4AF37]/20 text-[#8a6d1a] rounded font-medium">Decision Maker</span>}
                  </div>
                  <p className="text-xs text-gray-500">{ct.title || '—'} {ct.email ? `· ${ct.email}` : ''}</p>
                  {readyToContact && (
                    <button onClick={() => setOutreachContact(ct)} data-testid={`prepare-outreach-contact-${ct.id}`}
                      className="mt-1.5 flex items-center gap-1 text-[11px] font-medium text-[#0A1F44] hover:underline">
                      <Sparkles className="h-3 w-3 text-[#D4AF37]" /> Prepare Outreach
                    </button>
                  )}
                </div>
              );})}
            </div>

            <div>
              <h3 className="text-sm font-semibold text-[#0A1F44] flex items-center gap-1.5 mb-2"><Clock className="h-4 w-4" /> Activity</h3>
              <div className="flex gap-2 mb-2">
                <input value={activityText} onChange={e => setActivityText(e.target.value)} placeholder="Log a note or activity..." data-testid="activity-input"
                  className="flex-1 text-sm px-2 py-1.5 border border-gray-200 rounded-lg" onKeyDown={e => e.key === 'Enter' && logActivity()} />
                <button onClick={logActivity} data-testid="log-activity-btn" className="px-3 py-1.5 bg-gray-100 text-[#0A1F44] rounded-lg text-sm font-medium hover:bg-gray-200">Log</button>
              </div>
              {company.activities.length === 0 ? (
                <p className="text-xs text-gray-400">No activity logged yet.</p>
              ) : company.activities.map(a => (
                <div key={a.id} className={`text-xs text-gray-600 border-l-2 pl-2.5 mb-2 ${ACTIVITY_BORDER[a.activity_type] || 'border-gray-200'}`} data-testid={`activity-item-${a.id}`}>
                  <p className="font-medium text-[#0A1F44]">{a.action}</p>
                  <p className="text-gray-400">{a.created_at ? a.created_at.slice(0, 16).replace('T', ' ') : ''}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      {showAddContact && <AddContactModal companyId={companyId} onClose={() => setShowAddContact(false)} onCreated={() => { setShowAddContact(false); load(); }} />}
      {outreachContact && (
        <CRMOutreachModal company={company} contact={outreachContact} onClose={() => setOutreachContact(null)} onLogged={() => { load(); onUpdated && onUpdated(); }} />
      )}
    </div>
  );
};

const ACTIVITY_BORDER = {
  bounce: 'border-red-300', do_not_contact: 'border-amber-300', outreach_suppressed: 'border-amber-300', reply: 'border-blue-200',
};

const Field = ({ label, children }) => (
  <div>
    <label className="text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-1 block">{label}</label>
    {children}
  </div>
);

const AddContactModal = ({ companyId, onClose, onCreated }) => {
  const [form, setForm] = useState({ first_name: '', last_name: '', title: '', email: '', phone: '', linkedin_url: '', notes: '', is_decision_maker: false });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.first_name.trim()) { toast.error('First name is required'); return; }
    setSaving(true);
    try {
      await fetchWithAuth(`${API_URL}/api/crm/contacts`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...form, company_id: companyId }) });
      toast.success('Contact added');
      onCreated();
    } catch (e) {
      toast.error('Could not add contact');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" data-testid="add-contact-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6 space-y-3 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-bold text-[#0A1F44]">Add Contact</h3>
          <button onClick={onClose} data-testid="close-add-contact-modal"><X className="h-4 w-4 text-gray-400" /></button>
        </div>
        {['first_name', 'last_name', 'title', 'email', 'phone', 'linkedin_url'].map(field => (
          <input key={field} value={form[field]} onChange={e => setForm({ ...form, [field]: e.target.value })}
            placeholder={field === 'first_name' ? 'First name *' : field.replace('_', ' ')} data-testid={`add-contact-${field}`}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
        ))}
        <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Notes / source" rows={2}
          data-testid="add-contact-notes" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input type="checkbox" checked={form.is_decision_maker} onChange={e => setForm({ ...form, is_decision_maker: e.target.checked })} data-testid="add-contact-decision-maker" />
          Decision maker
        </label>
        <button onClick={submit} disabled={saving} data-testid="add-contact-submit"
          className="w-full py-2 bg-[#0A1F44] text-white rounded-lg text-sm font-medium hover:bg-[#0A1F44]/90 disabled:opacity-50">
          {saving ? 'Adding...' : 'Add Contact'}
        </button>
      </div>
    </div>
  );
};

export default CRMCompanyDetail;
